<template>
  <h1>ログイン</h1>
  <div class="d-flex justify-center">
    <v-card
      variant="flat"
      color="rgb(var(--v-theme-background))"
      class="signin-form-container">
      <v-form
        @submit.prevent="onSigninFormSubmit"
        class="signin-form"
      >
        <v-container
          id="tour-step1"
        >
          <v-row>
            <v-col>
              <v-list-subheader class="input-header">ログインID</v-list-subheader>
                <v-text-field
                  class="input-area"
                  variant="outlined"
                  v-model="loginId.value.value"
                  required
                  :error-messages="loginId.errorMessage.value"
                  hide-details="auto"
                  density="compact"
                ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-list-subheader class="input-header">パスワード</v-list-subheader>
              <v-text-field
                class="input-area"
                variant="outlined"
                v-model="password.value.value"
                required
                :error-messages="password.errorMessage.value"
                :type="show ? 'text' : 'password'"
                :append-inner-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="show = !show"
                hide-details="auto"
                density="compact"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex justify-center">
              <Button
                styling="Flat"
                type="submit"
                >ログイン</Button>
            </v-col>
          </v-row>
        </v-container>
        <v-container>
          <v-row>
            <v-col class="d-flex justify-center">
              <v-btn
                variant="text"
                @click="clickForgotIdOrPassword"
                id="tour-step3"
              >
                ログインIDまたはパスワードを忘れた場合
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card>

  <!-- ログインダイアログ -->
  <SelectChargeDialog
    ref="selectChargeDialog"
    @cancel="cancelSelectChargeDialog"
    @success="successSelectChargeDialog"
    v-model:open="openSelectChargeDialog"
    :customer="loginCustomer"
   />

  <!-- パスワードリセットダイアログ -->
  <ForgotIdOrPasswordDialog
    v-model:open="openForgotIdOrPasswordDialog"
    @success="successForgotIdOrPasswordDialog"
    @cancel="cancelForgotIdOrPasswordDialog" />
  </div>
</template>

<script setup lang="ts">
import { useForm, useField } from 'vee-validate'
import * as yup from 'yup'
import { useAuthFunc } from '@/utils/auth'
import { useRoute } from 'vue-router'
import { ref } from 'vue'
import * as api from '@/api/okaso-api'
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js'
import { jwtDecode } from 'jwt-decode'

import SelectChargeDialog from '@/components/organisms/dialogs/00000_SelectChargeDialog.vue'
import Button from '@/components/molecules/buttons/Button.vue'
import ForgotIdOrPasswordDialog from '@/components/organisms/dialogs/00000_ForgotIDOrPasswordDialog.vue'
import store from '@/store'
import { useGetCustomerFunc } from '@/services/getCustomer'
import { useFindFirstCustomerFunc } from '@/services/findFirstCustomer'

const route = useRoute()
const authUtil = useAuthFunc()

// subで得意作取得 -------
const {
  getCustomer,
  getCustomerOnResult,
  getCustomerOnError,
} = useGetCustomerFunc()
//-----------------------
const {
  findFirstCustomer,
  findFirstCustomerOnResult,
  findFirstCustomerOnError,
} = useFindFirstCustomerFunc()

// 担当者選択ダイアログ
const selectChargeDialog = ref<InstanceType<typeof SelectChargeDialog> | null>(null);
const openSelectChargeDialog = ref<boolean>(false)

// Create the form
const { handleSubmit, setErrors } = useForm()

// ログインユーザー情報（得意先）
const loginCustomer = ref<api.Customer>()

// Define fields
const loginId = useField<string>('loginId', yup.string().required('ログインIDを入力してください。'));
const password = useField<string>('password', yup.string().required('パスワードを入力してください'));

const onSigninFormSubmit = handleSubmit(async ():Promise<void> => {
  loginCustomer.value = undefined
  findFirstCustomer({ username: loginId.value.value })
})

findFirstCustomerOnResult((result) => {
  if(result.data){
    if (result.data.findFirstCustomer) {
      login()
    } else {
      setErrors({
        loginId: 'ログインIDまたはパスワードが違います。',
        password: 'ログインIDまたはパスワードが違います。',
      })
      cancelSelectChargeDialog()
    }
  }
})
findFirstCustomerOnError((error) => {
  setErrors({
    loginId: 'ログインIDまたはパスワードが違います。',
    password: 'ログインIDまたはパスワードが違います。',
  })
  cancelSelectChargeDialog()
})

const login = () => {
  // cognito設定
  const poolData = {
    UserPoolId: import.meta.env.VITE_POOL_ID || '',
    ClientId: import.meta.env.VITE_CLIENT_ID || '',
  }

  const userPool = new CognitoUserPool(poolData)

  const authenticationData = {
    Username: loginId.value.value,
    Password: password.value.value,
  }

  const authenticationDetails = new AuthenticationDetails(
    authenticationData
  )

  const userData = {
    Username: loginId.value.value,
    Pool: userPool,
  }


  const cognitoUser = new CognitoUser(userData)

  //ログイン処理
  cognitoUser.authenticateUser(authenticationDetails, {
    onSuccess: function(session) {
      // ログイン成功
      const token = session.getAccessToken().getJwtToken()
      // トークンからsubを取得
      const decoded = jwtDecode(token)
      if (decoded.sub) {
        const sub = decoded.sub
        authUtil.signin({
          token,
          sub,
        })
        // AuthSubを指定して得意先取得
        getCustomer()
      }
    },
    onFailure: function() {
      setErrors({
        loginId: 'ログインIDまたはパスワードが違います。',
        password: 'ログインIDまたはパスワードが違います。'
      })
      cancelSelectChargeDialog()
    }
  })
}

getCustomerOnResult((result) => {
  if (result.data?.findFirstCustomer) {
    loginCustomer.value = result.data?.findFirstCustomer as api.Customer
    // 担当者選択ダイアログを開く
    openSelectChargeDialog.value = true
  } else {
    loginCustomer.value = undefined
  }
})
getCustomerOnError((error) => {
  console.log(error.message)
  setErrors({
    loginId: 'ログインIDまたはパスワードが違います。',
    password: 'ログインIDまたはパスワードが違います。'
  })
  cancelSelectChargeDialog()
})

// 担当者選択ダイアログキャンセル
const cancelSelectChargeDialog = () => {
  if ( authUtil.signout() ) {
    // ログアウト
    openSelectChargeDialog.value = false
  }
}

// 担当者選択完了
const successSelectChargeDialog = (customer: api.Customer, staff: api.Staff) => {
  if (staff) {
    // ログイン中の担当者IDセット
    store.dispatch('loginUser/set', { customer, staff })
    // リダイレクト
    const redirectUrl = route.query.redirect?.toString()
    location.href = redirectUrl ? redirectUrl: '/'
  }
}

// パスワード入力欄
const show = ref(false);

// パスワードリセットダイアログの開閉
const openForgotIdOrPasswordDialog = ref<boolean>(false)

const cancelForgotIdOrPasswordDialog = ():void => {
  openForgotIdOrPasswordDialog.value = false;
}

const clickForgotIdOrPassword = ():void => {
  openForgotIdOrPasswordDialog.value = true;
}

// ログインIDの受け取り
const successForgotIdOrPasswordDialog = (loginId: string) => {
  cancelForgotIdOrPasswordDialog();
  openNewPasswordDialog.value = true;
}

// NEWパスワードダイアログの開閉
const openNewPasswordDialog = ref<boolean>(false);

const cancelNewPasswordDialog = ():void => {
  openNewPasswordDialog.value = false;
}

const successNewPasswordDialog = (data:any) => {
  if(data) {
    console.log('NEWパスワード : ' + data);
    // パスワードリセットダイアログを閉じる
    cancelNewPasswordDialog();
    // 完了ダイアログを開く
    successResetCompleatDialog();
  }
}

// パスワード設定完了ダイアログの開閉
const openResetCompleatDialog = ref<boolean>(false)

const successResetCompleatDialog = ():void => {
  openResetCompleatDialog.value = true;
}
</script>

<style lang="scss" scoped>
.signin-form-container {
  width: 524px
}

.input-header {
  font-size: 20px;
  font-weight: 700;
}
.input-area {
  font-size: 20px;

  &::v-deep(.v-field__field) {
    display: flex;
    align-items: center;
  }
  &::v-deep(.v-field__input) {
    font-size: 20px;
    line-height: 2;
    padding: 0 8px;

    input {
      margin: 0;
    }
  }

  &::v-deep(.v-input__control) {
    background-color: white;
  }
}
</style>
